import type { Types } from '@cornerstonejs/core';
import { utilities } from '@cornerstonejs/core';
import { logging } from '@cornerstonejs/metadata';
import { getOptions } from './options';
import type { LoaderXhrRequestError } from '../../types';
import extractMultipart from '../wadors/extractMultipart';
import { getImageQualityStatus } from '../wadors/getImageQualityStatus';
import getRetrieveValue from './getRetrieveValue';
import {
  DEFAULT_CHUNK_SIZE,
  DEFAULT_INITIAL_CHUNK_SIZE,
} from './retrieveDefaults';
import type {
  CornerstoneWadoRsLoaderOptions,
  StreamingData,
} from '../wadors/loadImage';

const log = logging.loaderLog.getLogger('streamRequest');

const { ProgressiveIterator } = utilities;

/**
 * Byte count at which the next decode of a streaming response becomes
 * worthwhile.
 *
 * The first decode and the ones after it wait for different amounts, for the
 * same reason a range retrieve fetches different sizes: the first decode buys
 * time to first image, and the rest buy refinement. `lastSize` is 0 until the
 * first decode has run, which is what distinguishes the two cases.
 *
 * @param lastSize - bytes held at the last decode, 0 if none has run
 * @param initialChunkSize - bytes to wait for before the first decode
 * @param chunkSize - new bytes to wait for before each later decode
 */
export function nextDecodeAt(
  lastSize: number,
  initialChunkSize: number,
  chunkSize: number
): number {
  return lastSize + (lastSize ? chunkSize : initialChunkSize);
}

/**
 * This function does a streaming parse from an http request, delivering
 * combined/subsequent parts of the result as iterations on a
 * ProgressiveIterator instance.
 *
 * @param url - to request and parse as either multipart or singlepart.
 * @param imageId - the imageId to be used in the returned detail object
 * @param defaultHeaders
 * @returns
 */
export default function streamRequest(
  url: string,
  imageId: string,
  defaultHeaders: Record<string, string> = {},
  options: CornerstoneWadoRsLoaderOptions = {}
) {
  const globalOptions = getOptions();
  const {
    retrieveOptions = {} as Types.RangeRetrieveOptions,
    streamingData = {} as StreamingData,
  } = options;

  // How much new data to accumulate before decoding the partial codestream
  // again. `minChunkSize` is the former name for this and is still honoured.
  //
  // chunkSize may be a callback over the image metadata, so it has to be
  // resolved to a number here: left as a function it would be truthy, flow into
  // the `lastSize + minChunkSize` comparison below as NaN, and disable the
  // threshold entirely - decoding every chunk received.
  const minChunkSize =
    getRetrieveValue<number>(imageId, retrieveOptions, 'chunkSize') ||
    getRetrieveValue<number>(imageId, retrieveOptions, 'minChunkSize') ||
    DEFAULT_CHUNK_SIZE;

  // How much data to accumulate before the *first* decode, which is a separate
  // size for the same reason it is on a range retrieve: the first decode buys
  // time to first image, and the ones after it buy refinement. Without this the
  // stream shows nothing until a full chunkSize has arrived.
  const initialChunkSize =
    getRetrieveValue<number>(imageId, retrieveOptions, 'initialChunkSize') ||
    DEFAULT_INITIAL_CHUNK_SIZE;

  const errorInterceptor = (err) => {
    if (typeof globalOptions.errorInterceptor === 'function') {
      const error = new Error('request failed') as LoaderXhrRequestError;
      globalOptions.errorInterceptor(error);
    }
  };

  // Make the request for the streamable image frame (i.e. HTJ2K)
  const loadIterator = new ProgressiveIterator('streamRequest');
  loadIterator.generate(async (iterator, reject) => {
    const beforeSendHeaders = await globalOptions.beforeSend?.(
      null,
      url,
      defaultHeaders,
      {}
    );
    const headers = Object.assign({}, defaultHeaders, beforeSendHeaders);

    Object.keys(headers).forEach(function (key) {
      if (headers[key] === null) {
        headers[key] = undefined;
      }
      if (key === 'Accept' && url.indexOf('accept=') !== -1) {
        headers[key] = undefined;
      }
    });

    try {
      const response = await fetch(url, {
        headers,
        signal: undefined,
      });

      // Response is expected to be a 200 status response
      if (response.status !== 200) {
        throw new Error(
          `Couldn't retrieve ${url} got status ${response.status}`
        );
      }
      const responseReader = response.body.getReader();
      const responseHeaders = response.headers;

      const contentType = responseHeaders.get('content-type');

      const totalBytes = Number(responseHeaders.get('Content-Length'));

      let readDone = false;
      let encodedData = streamingData.encodedData;
      // @ts-ignore
      let lastSize = streamingData.lastSize || 0;
      // @ts-ignore
      streamingData.isPartial = true;

      while (!readDone) {
        const { done, value } = await responseReader.read();
        encodedData = appendChunk(encodedData, value);
        if (!encodedData) {
          if (readDone) {
            throw new Error(`Done but no image frame available ${imageId}`);
          }
          continue;
        }
        readDone = done || encodedData.byteLength === totalBytes;
        if (
          !readDone &&
          encodedData.length <
            nextDecodeAt(lastSize, initialChunkSize, minChunkSize)
        ) {
          continue;
        }
        lastSize = encodedData.length;
        // @ts-ignore
        streamingData.isPartial = !done;
        const extracted = extractMultipart(
          contentType,
          encodedData,
          streamingData
        );
        const imageQualityStatus = getImageQualityStatus(
          retrieveOptions,
          readDone
        );
        const detail = {
          url,
          imageId,
          ...extracted,
          percentComplete: done
            ? 100
            : (extracted.pixelData?.length * 100) / totalBytes,
          imageQualityStatus,
          done: readDone,
        };

        // All of the image load events will be handled by the imageLoader
        // this simply delivers the raw data as it becomes available.
        iterator.add(detail, readDone);
      }
    } catch (err) {
      errorInterceptor(err);
      log.error(err);
      reject(err);
    }
  });

  return loadIterator.getNextPromise();
}

function appendChunk(existing: Uint8Array, chunk?: Uint8Array) {
  // that imageId
  if (!existing) {
    return chunk;
  }
  if (!chunk) {
    return existing;
  }
  const newDataArray = new Uint8Array(existing.length + chunk.length);
  newDataArray.set(existing, 0);
  newDataArray.set(chunk, existing.length);
  return newDataArray;
}
